import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useId, useMemo, useRef, useState, createContext } from "react";
import {
  AiNotifications,
  type NotificationFieldKey,
} from "@/components/submit/ai-notifications";



import { Button } from "@/components/ui/button";
import { getClaimReadiness, saveReadinessAnswers } from "@/lib/integrity/prepayment.functions";

import {
  AlertTriangle,
  Camera,
  Upload,
  Mail,
  CheckCircle2,
  ArrowRight,
  X,
  FileText,
  Copy,
  Check,
  Clock,
  Plus,
  ChevronDown,
  RefreshCw,
  CreditCard,
  Loader2,
  Lock,
  ShieldCheck,
  Car,
  Shield,
  Package,
  Target,
} from "lucide-react";
import { toast } from "sonner";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { StatusChip } from "@/components/status-chip";
import { AuthGate } from "@/components/auth-gate";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import {
  fileService,
  DuplicateUploadError,
  InvalidUploadError,
  type DuplicateUploadInfo,
} from "@/lib/services/fileService";
import { canBypassDuplicateInvoiceWarning, duplicateWarningViewFor } from "@/lib/duplicate-bypass";
import {
  decideConfirmationView,
  resolveReadinessView,
  customerEnterableFields,
  readinessMsRemaining,
  resetReadinessDeadline,
  submitOptionsForAction,
  READY_DISCLOSURE,
  READINESS_STALL_MS,
  READINESS_CHECKING_BODY,
  READINESS_UNAVAILABLE_BODY,
  READINESS_UNAVAILABLE_TITLE,
  TESTING_CONTINUE_DISCLOSURE,
  type ReadinessView,
  type SubmissionMode,
} from "@/lib/submission-mode";
import {
  authorizeDuplicateInvoiceBypass,
  recordDuplicateInvoiceBypass,
} from "@/lib/duplicate-bypass.functions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { aliasForUserId } from "@/lib/email-intake/alias";
import { reviewToCase } from "@/hooks/use-real-reviews";
import type { Review } from "@/lib/db/models";
import type { Case, SubmissionMethod, CaseDetails } from "@/lib/cases";

/**
 * Customer-provided Service Provider Information the readiness gate can accept.
 * Every value is Customer Provided · Unverified and never overwrites OCR.
 * Adding a new readiness-highlightable field means adding it here only.
 */
type ReadinessAnswers = {
  vendor?: string;
  vehicleVin?: string;
  zip?: string;
  vendorPhone?: string;
  vendorAddress?: string;
  vendorCity?: string;
  vendorState?: string;
};

import { normalizeVin, vinValidationError } from "@/lib/vin";
import { LegalConsent } from "@/components/legal-consent";
import { sendAppEmail } from "@/lib/email/send-app";
import { checkInvoiceUploadAllowance } from "@/lib/upload-guard.functions";
import { SubmissionProgress } from "@/components/submit/submission-progress";
import {
  SUBMIT_TIMEOUT_MS,
  STALL_NOTICE_MS,
  buildSubmitDiagnostic,
  COMPLETION_HOLD_MS,
  classifySubmitError,
  newSubmitRequestId,
  stageDef,
  submitErrorMessage,
  type SubmitErrorKind,
  type SubmitStageId,
} from "@/lib/submit-progress";

import {
  REVIEW_PRICE_LABEL,
  
  NEGOTIATION_PRICE_LABEL,
  NEGOTIATION_TYPICAL_RANGE_LABEL,
  NEGOTIATION_PRICING_ESTIMATE_DISCLAIMER,
} from "@/lib/pricing";

export const Route = createFileRoute("/submit")({
  /* `claim` reopens an existing saved submission (draft resume). It never
     creates a new review — the page simply rehydrates the saved one. */
  validateSearch: (search: Record<string, unknown>): { claim?: string } => ({
    claim: typeof search.claim === "string" && search.claim.trim() ? search.claim.trim() : undefined,
  }),
  head: () => ({
    meta: [
      { title: "ClaimGuard | Submit an Invoice for Review" },
      {
        name: "description",
        content:
          "Send an invoice by photo, upload, or email. We'll review it, research issues, compare local pricing, and prepare a professional findings package.",
      },
    ],
  }),
  component: () => (
    <AuthGate>
      <SubmitPage />
    </AuthGate>
  ),
});

type SubmitStage = "idle" | SubmitStageId | "done";

function generateFileNumber(): string {
  const y = new Date().getFullYear();
  const seq = String(Math.floor(Math.random() * 999999) + 1).padStart(6, "0");
  return `INV-${y}-${seq}`;
}

const METHOD_LABEL: Record<SubmissionMethod, string> = {
  photo: "Photo",
  upload: "File upload",
  email: "Email forwarding",
};

function SubmitPage() {
  const { user } = useAuth();
  const { claim: resumeClaim } = Route.useSearch();
  const [method, setMethod] = useState<SubmissionMethod>("upload");
  const [files, setFiles] = useState<File[]>([]);
  const [details, setDetails] = useState<CaseDetails>({ urgency: "normal" });

  const [claimRef, setClaimRef] = useState<string>("");
  const [stage, setStage] = useState<SubmitStage>("idle");
  const [confirmed, setConfirmed] = useState<Case | null>(null);

  /* --- Resume a saved submission ----------------------------------------
     Reopening a draft rehydrates the EXISTING review: no new review row, no
     re-upload, no re-run of OCR or AI. The saved claim is dropped straight
     into the confirmation/readiness slot so the customer lands exactly where
     they left off. */
  useEffect(() => {
    if (!resumeClaim || !user) return;
    let cancelled = false;
    void (async () => {
      const { data: review } = await supabase
        .from("reviews")
        .select("*")
        .eq("file_number", resumeClaim)
        .maybeSingle();
      if (cancelled || !review) return;
      const { data: rf } = await supabase
        .from("review_files")
        .select("file_name,file_size,mime_type")
        .eq("review_id", (review as Review).id);
      const caseObj = reviewToCase(
        review as Review,
        (rf ?? []).map((f) => ({
          name: f.file_name as string,
          size: (f.file_size as number | null) ?? 0,
          type: (f.mime_type as string | null) ?? "",
        })),
      );
      if (cancelled) return;
      setMethod(caseObj.method);
      setDetails((d) => ({ ...d, ...caseObj.details }));
      setConfirmed(caseObj);
      setStage("done");
    })();
    return () => {
      cancelled = true;
    };
  }, [resumeClaim, user]);

  const [duplicate, setDuplicate] = useState<DuplicateUploadInfo | null>(null);
  const [duplicateFileNumber, setDuplicateFileNumber] = useState<string | null>(null);
  /* Explicit routing mode for THIS submission. "testing" is only reachable
     from "Submit Again for Testing" on an authorized account; it skips the
     STOPPING behaviour of the readiness gate — never the evaluation,
     warnings, requirements, duplicate detection or payment. */
  const [submissionMode, setSubmissionMode] = useState<SubmissionMode>("live");


  const submitInFlight = useRef(false);
  const canBypassDuplicate = canBypassDuplicateInvoiceWarning(user);

  /* --- Submission progress / stall recovery ------------------------------
     `startedAt` drives the elapsed clock and the stall + timeout watchdogs.
     `existingReview` survives a recoverable failure so a retry REUSES the
     review that was already created instead of inserting a second one. */
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const existingReview = useRef<{ id: string; fileNumber: string } | null>(null);
  const uploadedFileKeys = useRef<Set<string>>(new Set());
  const requestId = useRef<string>(newSubmitRequestId());
  const abandoned = useRef(false);

  /* --- Same-page readiness bridge ---------------------------------------
     The readiness panel lives inside <ConfirmationState />, but the fields a
     customer can resolve by typing already exist in Optional Claim
     Information. Rather than build a second form, the panel reports which
     fields are still required plus the save action, and the existing form
     highlights and saves them in place. Presentation wiring only — the
     readiness rules and the server guard are untouched. */
  const [readinessFields, setReadinessFields] = useState<string[]>([]);
  /* Presentation-only: true once the AI extraction / readiness evaluation has
     actually finished for this claim. Error styling is never shown before it. */
  const [extractionAttempted, setExtractionAttempted] = useState(false);
  const [savingRequired, setSavingRequired] = useState(false);
  const saveRequiredRef = useRef<
    ((v: ReadinessAnswers) => Promise<void>) | null
  >(null);
  /* Additional Details is customer-entered only: extraction candidates are
     NEVER written into these inputs. OCR/readiness continue running for
     internal processing; the form simply does not consume their values. */
  const handleReadinessState = useCallback(
    (s: {
      fields: string[];
      save: (v: ReadinessAnswers) => Promise<void>;
      attempted?: boolean;
    }) => {
      saveRequiredRef.current = s.save;
      setExtractionAttempted(!!s.attempted);
      setReadinessFields((prev) =>
        prev.length === s.fields.length && prev.every((f, i) => f === s.fields[i])
          ? prev
          : s.fields,
      );
    },
    [],
  );





  // Private submission email — stable alias derived from the user id. Shared
  // with /dashboard and the server-side email intake matcher.
  const privateEmail = useMemo(() => aliasForUserId(user?.id), [user?.id]);

  const submitting = stage !== "idle" && stage !== "done";
  const activeStage: SubmitStageId | null =
    stage === "idle" || stage === "done" ? null : stage;
  const canSubmit = !submitting && (method === "email" || files.length > 0);

  // Elapsed clock — 1s tick, only while a submission is genuinely running.
  useEffect(() => {
    if (!startedAt) return;
    setElapsedMs(Date.now() - startedAt);
    const t = window.setInterval(() => setElapsedMs(Date.now() - startedAt), 1000);
    return () => window.clearInterval(t);
  }, [startedAt]);

  // Hard ceiling: stop the spinner and offer recovery. The underlying work is
  // never cancelled and any review already created is reused on retry.
  useEffect(() => {
    if (!startedAt || !submitting) return;
    const t = window.setTimeout(() => {
      abandoned.current = true;
      failSubmission("timeout", null);
    }, SUBMIT_TIMEOUT_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startedAt, submitting]);

  const stalled = submitting && elapsedMs >= STALL_NOTICE_MS;

  function logSubmitFailure(kind: SubmitErrorKind, stageId: SubmitStageId | null) {
    const diagnostic = buildSubmitDiagnostic({
      stage: stageId,
      code: kind,
      reviewId: existingReview.current?.id ?? null,
      requestId: requestId.current,
    });
    // Safe, PII-free diagnostic — no stack traces, secrets or provider detail.
    console.error("[submit] failure", diagnostic);
    void import("@/lib/monitoring").then(({ captureException }) =>
      captureException(new Error(`submit_failed:${kind}`), {
        source: "server_function",
        location: "submit:progress",
        userId: user?.id,
        tags: {
          stage: String(diagnostic.stage ?? "unknown"),
          request_id: diagnostic.requestId,
          ...(diagnostic.reviewId ? { review_id: diagnostic.reviewId } : {}),
        },
      }),
    );
  }

  function failSubmission(kind: SubmitErrorKind, stageId: SubmitStageId | null) {
    logSubmitFailure(kind, stageId);
    setSubmitError(submitErrorMessage(kind));
    setStage("idle");
    setStartedAt(null);
  }

  function saveAndReturnLater() {
    setSubmitError(null);
    setStage("idle");
    setStartedAt(null);
    toast.success(
      existingReview.current
        ? "Your review is saved. You can finish it any time from your dashboard."
        : "Your invoice and details are saved on this page. You can finish any time.",
    );
  }


  function onFiles(list: FileList | null) {
    if (!list) return;
    setFiles((prev) => [...prev, ...Array.from(list)].slice(0, 10));
  }


  /**
   * Pre-submit duplicate detection. Runs for EVERY account and is never
   * disabled — the only difference for the allowlisted testing account is
   * that the warning offers an explicit "Submit Again for Testing" action.
   */
  async function findDuplicate(): Promise<DuplicateUploadInfo | null> {
    if (!user) return null;
    for (const f of files) {
      let hash: string | null = null;
      try {
        hash = await fileService.computeFileHash(f);
      } catch {
        hash = null;
      }
      if (!hash) continue;
      const dupe = await fileService.findDuplicateForUser(user.id, hash);
      if (dupe) return dupe;
    }
    return null;
  }

  async function submit(options?: { bypassDuplicate?: boolean; mode?: SubmissionMode }) {
    let reachedDone = false;
    // Mirrors the React stage state synchronously so error classification can
    // name the stage that actually failed.
    let failedStage: SubmitStageId = "securing";
    const goToStage = (s: SubmitStageId) => {
      failedStage = s;
      setStage(s);
    };

    // onClick handlers pass a MouseEvent; only an explicit object flag counts.
    const bypassDuplicate = options?.bypassDuplicate === true;
    // Mode is client-only and never reaches the server: it can only relax the
    // client stop, and only for the authorized duplicate-testing path.
    const mode: SubmissionMode =
      options?.mode === "testing" && bypassDuplicate && canBypassDuplicate ? "testing" : "live";
    setSubmissionMode(mode);


    // Sync duplicate-submit guard — protects against double-clicks on slow
    // networks (where `stage` state has not yet propagated). Stripe
    // idempotency handled downstream; this prevents duplicate DB rows.
    if (submitInFlight.current) return;
    if (!user) {
      toast.error("Please sign in to submit an invoice.");
      return;
    }
    submitInFlight.current = true;
    // Stage 1 begins as soon as the click is accepted: duplicate detection and
    // the upload allowance check are the real "securing" work.
    abandoned.current = false;
    setSubmitError(null);
    setStage("securing");
    setStartedAt(Date.now());
    try {

    if (method === "email") {
      toast.info(
        "Email submission is coming soon. Please forward invoices to your private email once enabled.",
      );
      return;
    }
    if (files.length === 0) {
      toast.error("Please upload at least one invoice file.");
      return;
    }

    if (bypassDuplicate) {
      // Server is the authorization boundary. A forged client flag gets
      // rejected here before anything is written.
      if (!duplicate) {
        toast.error("No duplicate to confirm.");
        return;
      }
      try {
        await authorizeDuplicateInvoiceBypass({
          data: {
            fileHash: duplicate.fileHash,
            existingReviewId: duplicate.existingReviewId,
            existingFileName: duplicate.existingFileName,
            existingUploadedAt: duplicate.existingUploadedAt,
          },
        });
      } catch {
        setDuplicate(null);
        toast.error("This account isn't authorized to resubmit a duplicate invoice.");
        return;
      }
    } else {
      const dupe = await findDuplicate();
      if (dupe) {
        let priorFileNumber: string | null = null;
        try {
          const { data: prior } = await supabase
            .from("reviews")
            .select("file_number")
            .eq("id", dupe.existingReviewId)
            .maybeSingle();
          priorFileNumber = prior?.file_number ?? null;
        } catch {
          priorFileNumber = null;
        }
        setDuplicateFileNumber(priorFileNumber);
        setDuplicate(dupe);
        setStage("idle");
        return;
      }
    }


    // Rate limit preflight — cheap DB round-trip; prevents upload flooding.
    try {
      const gate = await checkInvoiceUploadAllowance();
      if (!gate.ok) {
        toast.error(gate.message);
        return;
      }
    } catch {
      // Fail open — never block a legitimate customer on limiter infra hiccup.
    }

    const totalRaw = details.totalAmount?.replace(/[^0-9.]/g, "");
    const totalNum = totalRaw ? Number(totalRaw) : undefined;
    // FastTrack is resolved ONCE here: a submission with no Optional Claim
    // Information is functionally FastTrack, so it is auto-selected silently
    // (no toast, modal or extra click) and persisted with the review.
    const { resolveFastTrackSelection } = await import("@/lib/fasttrack-auto");
    const decision = resolveFastTrackSelection(false, {
      insuranceCompany: details.insuranceCompany,
      claimNumber: claimRef,
      adjusterName: details.adjusterName,
      adjusterEmail: details.adjusterEmail,
      vehicleDescription: details.vehicleDescription,
      vehicleVin: details.vehicleVin,
      storageReason: details.storageReason,
      reviewFocus: details.reviewFocus,
      customerContext: details.additionalInfo,
    });
    const effectiveFastTrack = decision.fastTrack;
    const claimContext = decision.context;





    // Retry reuses the review created by the previous attempt — a recoverable
    // failure must never leave a second review behind.
    const file_number = existingReview.current?.fileNumber ?? generateFileNumber();
    const bucket = method === "photo" ? "photos" : "invoices";

    try {
      goToStage("creating");
      const { trackEvent } = await import("@/lib/analytics");
      void trackEvent({
        event: "invoice_upload_started",
        userId: user.id,
        properties: {
          method,
          fast_track: effectiveFastTrack,
          file_count: files.length,
        },
      });
      let review: { id: string } | null = existingReview.current
        ? { id: existingReview.current.id }
        : null;
      if (!review) {
        const { data: created, error: revErr } = await supabase
          .from("reviews")
          .insert({
            customer_id: user.id,
            file_number,
            status: "received",
            submission_method: method,
            fast_track: effectiveFastTrack,
            // Vendor / ZIP the customer typed are claim CONTEXT, not financial
            // facts: they satisfy the readiness gate but never override OCR.
            vendor: details.vendor || null,
            zip: details.zip || null,
            vendor_phone: details.vendorPhone || null,
            vendor_address: details.vendorAddress || null,
            vendor_city: details.vendorCity || null,
            vendor_state: details.vendorState || null,

            invoice_date: effectiveFastTrack ? null : details.invoiceDate || null,
            invoice_type: effectiveFastTrack ? null : details.invoiceType || null,
            total_amount: effectiveFastTrack ? null : (totalNum ?? null),
            urgency: effectiveFastTrack ? null : details.urgency || null,
            notes: null,
            ...claimContext,

          })
          .select()
          .single();
        if (revErr || !created) throw revErr ?? new Error("no review");
        review = created;
        existingReview.current = { id: created.id, fileNumber: file_number };
      }

      goToStage("uploading");
      let successfulUploads = uploadedFileKeys.current.size;
      const uploadFailures: string[] = [];
      for (const f of files) {
        // Skip files already persisted by an earlier attempt.
        const key = `${f.name}:${f.size}:${f.lastModified}`;
        if (uploadedFileKeys.current.has(key)) continue;
        try {
          const row = await fileService.upload({
            bucket,
            userId: user.id,
            reviewId: review.id,
            file: f,
            kind: method === "photo" ? "photo" : "invoice",
            allowDuplicate: bypassDuplicate,
          });
          if (row) {
            successfulUploads += 1;
            uploadedFileKeys.current.add(key);
          }
        } catch (e) {
          if (e instanceof DuplicateUploadError) {
            uploadFailures.push(`${f.name}: duplicate of an earlier upload`);
            toast.warning(

              `"${f.name}" looks identical to an invoice you already uploaded. You can continue with the existing review, or attach a different file.`,
              { duration: 8000 },
            );
            continue;
          }
          if (e instanceof InvalidUploadError) {
            uploadFailures.push(`${f.name}: ${e.message}`);
            toast.error(e.message);
            continue;
          }
          uploadFailures.push(`${f.name}: ${(e as Error).message ?? "upload failed"}`);
          console.error("upload failed", e);
          import("@/lib/monitoring").then(({ captureException }) =>
            captureException(e, {
              source: "server_function",
              location: "submit:upload",
              userId: user.id,
              tags: { review_id: review.id, method },
            }),
          );
          toast.error(`We couldn't upload ${f.name}. Please try again.`);
        }
      }



      // Hard gate: a review MUST have at least one review_files row before
      // AI processing starts. Otherwise OCR loads 0 files and the pipeline
      // gets stuck at "Reading Invoice" (which is exactly the incident we
      // just fixed). Do NOT write the "file_uploaded" timeline event or
      // enqueue the pipeline until we've confirmed persistence.
      if (successfulUploads === 0) {
        // Surface the recoverable error panel (Try Again / Save & Return
        // Later) rather than a bare toast.
        failSubmission("upload", "uploading");
        // This review is cancelled below, so a retry must start a new one.
        existingReview.current = null;
        uploadedFileKeys.current = new Set();

        const detail = uploadFailures.length
          ? ` (${uploadFailures.join("; ")})`
          : "";
        toast.error(
          `No files were saved for this review${detail}. Please try again or attach a different file.`,
          { duration: 10000 },
        );

        // Best-effort: mark the review so staff can clean it up.
        await supabase
          .from("reviews")
          .update({ status: "cancelled" })
          .eq("id", review.id);
        await supabase.from("timeline_events").insert({
          review_id: review.id,
          event_type: "upload_failed",
          title: "Upload failed",
          description: `No review_files rows were persisted. Failures: ${
            uploadFailures.join("; ") || "unknown"
          }. AI processing not started.`,
          actor_id: user.id,
        });
        const { trackEvent } = await import("@/lib/analytics");
        void trackEvent({
          event: "invoice_upload_failed",
          userId: user.id,
          properties: {
            method,
            fast_track: effectiveFastTrack,
            file_count: files.length,
            reason: "no_files_persisted",
          },
        });
        return;
      }

      if (bypassDuplicate && duplicate) {
        try {
          await recordDuplicateInvoiceBypass({
            data: {
              fileHash: duplicate.fileHash,
              existingReviewId: duplicate.existingReviewId,
              existingFileName: duplicate.existingFileName,
              existingUploadedAt: duplicate.existingUploadedAt,
              existingFileNumber: duplicateFileNumber,
              newReviewId: review.id,
              newFileNumber: file_number,
            },
          });
        } catch (e) {
          console.error("duplicate bypass audit failed", e);
        }
        setDuplicate(null);
        setDuplicateFileNumber(null);
      }

      goToStage("saving");
      await supabase.from("timeline_events").insert([
        {
          review_id: review.id,
          event_type: "submitted",
          title: "Review submitted",
          description: `Submitted via ${method}`,
          actor_id: user.id,
        },
        {
          review_id: review.id,
          event_type: "file_uploaded",
          title: "File uploaded",
          description: `${successfulUploads} file${successfulUploads === 1 ? "" : "s"} attached${
            uploadFailures.length
              ? ` (${uploadFailures.length} skipped: ${uploadFailures.join("; ")})`
              : ""
          }`,
          actor_id: user.id,
        },
        {
          review_id: review.id,
          event_type: "awaiting_payment",
          title: "Awaiting payment",
          description: "Your review will begin as soon as payment is completed.",
          actor_id: user.id,
        },
      ]);

      void trackEvent({
        event: "invoice_upload_completed",
        userId: user.id,
        properties: {
          review_id: review.id,
          method,
          fast_track: effectiveFastTrack,
          file_count: successfulUploads,
        },
      });

      /* PAYMENT GATE — the AI pipeline is NOT started here.
         Creating a review only stores the submission. OCR, invoice
         intelligence, market research, legal research, vendor intelligence,
         AI findings, the report and every customer notification are queued
         exactly once by `finalizeBillingPaid` after Stripe confirms payment.
         The review therefore stays at its "awaiting payment" status
         (`submitted`) until then. */

      const caseObj: Case = {
        id: file_number,
        method,
        fastTrack: effectiveFastTrack,
        details: {
          vendor: details.vendor,
          invoiceDate: details.invoiceDate,
          invoiceType: details.invoiceType,
          totalAmount: details.totalAmount,
          urgency: details.urgency,
          timeframe: details.timeframe,
          notes: undefined,
          insuranceCompany: claimContext.insurance_company ?? undefined,
          claimNumber: claimContext.claim_number ?? undefined,
          adjusterName: claimContext.adjuster_name ?? undefined,
          adjusterEmail: claimContext.adjuster_email ?? undefined,
          vehicleDescription: claimContext.vehicle_description ?? undefined,
          storageReason: claimContext.storage_reason ?? undefined,
          reviewFocus: claimContext.review_focus ?? undefined,
          additionalInfo: claimContext.customer_context ?? undefined,

        },
        files: files.map((f) => ({ name: f.name, size: f.size, type: f.type })),
        createdAt: new Date().toISOString(),
        status: "received",
        negotiationChosen: false,
      };

      // Final pre-checkout step: the confirmation screen owns the real Stripe
      // checkout, so this stage completes when that screen is ready to render.
      goToStage("ready");
      setStage("done");
      reachedDone = true;
      setStartedAt(null);
      setSubmitError(null);
      // Submission finished — a fresh attempt is a brand new submission.
      existingReview.current = null;
      uploadedFileKeys.current = new Set();
      // Brief readability hold so the customer sees the completion
      // confirmation before the payment step replaces the panel. The
      // submission is already complete; nothing real is delayed or gated.
      await new Promise((r) => setTimeout(r, COMPLETION_HOLD_MS));
      setConfirmed(caseObj);
      toast.success(`Review ${file_number} created.`);

      // Best-effort branded "invoice received" email — never blocks UX.
      if (user.email) {
        void sendAppEmail({
          templateName: "invoice-uploaded",
          recipientEmail: user.email,
          idempotencyKey: `invoice-uploaded:${review.id}`,
          templateData: {
            customerName:
              (user.user_metadata as { full_name?: string } | undefined)
                ?.full_name ?? user.email,
            reviewRef: file_number,
            fileCount: files.length,
            submittedAt: new Date().toLocaleString(),
            reviewUrl: `${window.location.origin}/case/${review.id}`,
          },
        });
      }
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const kind = classifySubmitError(e, failedStage);
      failSubmission(kind, failedStage);
      const { trackEvent } = await import("@/lib/analytics");
      void trackEvent({
        event: "invoice_upload_failed",
        userId: user.id,
        properties: {
          method,
          fast_track: effectiveFastTrack,
          file_count: files.length,
          error: kind,
        },
      });
    }
    } finally {
      submitInFlight.current = false;
      if (!reachedDone) {
        // Every early return (duplicate found, rate limit, no files saved)
        // must leave the form usable again.
        setStage((s) => (s === "done" ? s : "idle"));
        setStartedAt(null);
      }
    }

  }

  function resetForAnother() {
    setConfirmed(null);
    setFiles([]);
    
    setDetails({ urgency: "normal" });
    setClaimRef("");
    setMethod("upload");
    setStage("idle");
    setStartedAt(null);
    setSubmitError(null);
    existingReview.current = null;
    uploadedFileKeys.current = new Set();
    requestId.current = newSubmitRequestId();
  }


  return (
    <div className="min-h-dvh bg-background">
      <SiteHeader />
      <div className="container-page pb-0 pt-12">
        <div className="mx-auto max-w-3xl">
          {/* The Submit page never hands the customer off to a standalone
              confirmation/checking page. Once a claim exists, the readiness
              experience replaces the Step 3 panel in place while the upload
              list, method cards and Optional Claim Information stay usable. */}
          <SubmissionForm
            method={method}
            setMethod={setMethod}
            files={files}
            setFiles={setFiles}
            onFiles={onFiles}
            details={details}
            setDetails={setDetails}
            claimRef={claimRef}
            setClaimRef={setClaimRef}
            privateEmail={privateEmail}
            canSubmit={canSubmit}
            submitting={submitting}
            stage={stage}
            activeStage={activeStage}
            elapsedMs={elapsedMs}
            stalled={stalled}
            submitError={submitError}
            onSaveForLater={saveAndReturnLater}
            submit={submit}
            requiredFields={readinessFields}
            resumed={!!resumeClaim}
            extractionAttempted={extractionAttempted}
            savingRequired={savingRequired}

            onSaveRequired={
              saveRequiredRef.current
                ? async (v) => {
                    setSavingRequired(true);
                    try {
                      await saveRequiredRef.current?.(v);
                    } finally {
                      setSavingRequired(false);
                    }
                  }
                : undefined
            }
            confirmation={
              confirmed ? (
                <ConfirmationState
                  c={confirmed}
                  onAnother={resetForAnother}
                  mode={submissionMode}
                  onReadinessState={handleReadinessState}
                />
              ) : null
            }
          />
        </div>
      </div>

      <div className="[&>footer]:mt-[76px]">
        <SiteFooter />
      </div>

      <DuplicateInvoiceDialog
        duplicate={duplicate}
        priorFileNumber={duplicateFileNumber}
        canBypass={canBypassDuplicate}
        onCancel={() => {
          setDuplicate(null);
          setDuplicateFileNumber(null);
        }}
        onSubmitAgain={() => {
          void submit(submitOptionsForAction("submit_testing", { canBypass: canBypassDuplicate }));
        }}
        onSubmitLive={() => {
          // Live: duplicate allowance only. Never the testing mode — the
          // readiness gate stops this submission exactly like a real customer's.
          void submit(submitOptionsForAction("submit_live", { canBypass: canBypassDuplicate }));
        }}

      />
    </div>
  );
}

/* ---------------- Duplicate invoice warning ---------------- */

function DuplicateInvoiceDialog(props: {
  duplicate: DuplicateUploadInfo | null;
  priorFileNumber: string | null;
  canBypass: boolean;
  onCancel: () => void;
  onSubmitAgain: () => void;
  onSubmitLive: () => void;
}) {
  const { duplicate, priorFileNumber, canBypass, onCancel, onSubmitAgain, onSubmitLive } =
    props;

  // Copy comes from the shared presentation contract so UI and tests agree.
  const view = duplicateWarningViewFor(canBypass);
  if (!duplicate) return null;
  const uploadedAt = duplicate.existingUploadedAt
    ? new Date(duplicate.existingUploadedAt).toLocaleString()
    : null;

  return (
    <AlertDialog open onOpenChange={(open) => { if (!open) onCancel(); }}>
      <AlertDialogContent data-testid="duplicate-invoice-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{view.title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-left">
              <p>{view.message}</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>
                  <span className="font-medium text-navy">Matching file:</span>{" "}
                  {duplicate.existingFileName}
                </li>
                {priorFileNumber ? (
                  <li>
                    <span className="font-medium text-navy">Previous file number:</span>{" "}
                    {priorFileNumber}
                  </li>
                ) : null}
                {uploadedAt ? (
                  <li>
                    <span className="font-medium text-navy">Previously submitted:</span>{" "}
                    {uploadedAt}
                  </li>
                ) : null}
              </ul>
              {view.testingNote ? <p>{view.testingNote}</p> : null}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 sm:justify-between">
          <Link
            to="/case/$id"
            params={{ id: duplicate.existingReviewId }}
            className="inline-flex h-10 items-center justify-center rounded-md border border-input px-4 text-sm font-medium"
          >
            View Existing Review
          </Link>
          <div className="flex flex-wrap gap-2">
            <AlertDialogCancel onClick={onCancel}>Replace File or Add a Missing Page</AlertDialogCancel>
            {canBypass ? (
              <>
                <AlertDialogAction
                  data-testid="duplicate-bypass-action"
                  onClick={onSubmitAgain}
                >
                  Submit Again for Testing
                </AlertDialogAction>
                <AlertDialogAction
                  data-testid="duplicate-live-action"
                  onClick={onSubmitLive}
                >
                  Submit As Live Invoice
                </AlertDialogAction>
              </>
            ) : null}
          </div>

        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ---------------- Submission form ---------------- */

function SubmissionForm(props: {
  method: SubmissionMethod;
  setMethod: (m: SubmissionMethod) => void;
  files: File[];
  setFiles: (f: File[]) => void;
  onFiles: (l: FileList | null) => void;
  details: CaseDetails;
  setDetails: (d: CaseDetails) => void;
  claimRef: string;
  setClaimRef: (v: string) => void;
  privateEmail: string;
  canSubmit: boolean;
  submitting: boolean;
  stage: SubmitStage;
  activeStage: SubmitStageId | null;
  elapsedMs: number;
  stalled: boolean;
  submitError: string | null;
  onSaveForLater: () => void;
  submit: () => void;
  /** Readiness panel rendered in place of the Step 3 submit panel. */
  confirmation?: React.ReactNode;
  /**
   * Customer-enterable requirement fields still unresolved. Today the readiness
   * engine emits "vendor" and "zip"; "vendorPhone" / "vendorAddress" are wired
   * through the same highlighting system so a future rule needs no redesign.
   */
  requiredFields?: string[];
  /** AI extraction has finished; only then may a field show error styling. */
  extractionAttempted?: boolean;
  savingRequired?: boolean;
  onSaveRequired?: (v: ReadinessAnswers) => Promise<void>;
  /** Reopened saved submission — header, status card and auto-scroll only. */
  resumed?: boolean;


}) {
  const {
    method,
    setMethod,
    files,
    setFiles,
    onFiles,
    details,
    setDetails,
    claimRef,
    setClaimRef,
    privateEmail,
    canSubmit,
    submitting,
    stage,
    activeStage,
    elapsedMs,
    stalled,
    submitError,
    onSaveForLater,
    submit,
    confirmation,
    requiredFields = [],
    extractionAttempted = false,
    savingRequired = false,
    onSaveRequired,
    resumed = false,
  } = props;




  const needsVendor = requiredFields.includes("vendor");
  const needsZip = requiredFields.includes("zip");

  // Future readiness rules can require these without any layout change.
  const needsVendorPhone = requiredFields.includes("vendorPhone");
  const needsVendorAddress = requiredFields.includes("vendorAddress");
  const needsVendorCity = requiredFields.includes("vendorCity");
  const needsVendorState = requiredFields.includes("vendorState");
  const hasRequiredFields =
    needsVendor ||
    needsZip ||
    needsVendorPhone ||
    needsVendorAddress ||
    needsVendorCity ||
    needsVendorState;
  const vendorFieldRef = useRef<HTMLDivElement>(null);
  const zipFieldRef = useRef<HTMLDivElement>(null);

  /* Which required fields the customer still has to complete, in page order.
     Presentation only — the server remains the sole judge of readiness. */
  const [attemptedRecheck, setAttemptedRecheck] = useState(false);
  /* VIN is optional: the "incomplete VIN" message only appears once the
     customer leaves the field or tries to continue. */
  const [vinTouched, setVinTouched] = useState(false);
  const contextValue = (f: string) =>
    (f === "vendor"
      ? details.vendor
      : f === "zip"
        ? details.zip
        : f === "vendorPhone"
          ? details.vendorPhone
          : details.vendorAddress) ?? "";
  const contextFilled = (f: string) =>
    f === "zip"
      ? /^\d{5}(-\d{4})?$/.test(contextValue(f).trim())
      : contextValue(f).trim().length > 0;
  const requiredInOrder = CONTEXT_FIELD_ORDER.filter((f) => requiredFields.includes(f));
  const unresolvedRequired = requiredInOrder.filter((f) => !contextFilled(f));
  const jumpTarget = unresolvedRequired[0] ?? requiredInOrder[0] ?? "vendor";
  /* Every required value is in. Presentation only: fields stay visible and
     editable; verification just switches them to success styling. */
  const requiredComplete = hasRequiredFields && unresolvedRequired.length === 0;
  /* Error styling only ever means "the canonical readiness object still needs
     this value from the customer" — never "you haven't typed anything yet".
     A field that readiness already satisfies from internal invoice evidence is
     NOT required and must stay neutral even while blank. */
  const showValidation = extractionAttempted;
  const stillRequired = (f: string) =>
    requiredFields.includes(f) && !contextFilled(f);
  const fieldError = (f: string) =>
    (showValidation || attemptedRecheck) && stillRequired(f)
      ? f === "zip"
        ? "We couldn't confidently identify the service location ZIP code."
        : "We couldn't confidently identify the towing/storage company."

      : undefined;



  // Scroll the first missing field into view once it becomes required.
  useEffect(() => {
    if (!hasRequiredFields || typeof window === "undefined") return;
    const target = needsVendor ? vendorFieldRef.current : zipFieldRef.current;
    const t = window.setTimeout(
      () => target?.scrollIntoView({ behavior: "smooth", block: "center" }),
      250,
    );
    return () => window.clearTimeout(t);
  }, [hasRequiredFields, needsVendor]);

  // After a recheck completes, glide down to the updated readiness result.
  const wasSaving = useRef(false);
  useEffect(() => {
    if (savingRequired) {
      wasSaving.current = true;
      return;
    }
    if (!wasSaving.current || typeof document === "undefined") return;
    wasSaving.current = false;
    const t = window.setTimeout(() => {
      document
        .querySelector('[data-testid="submit-readiness-panel"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => window.clearTimeout(t);
  }, [savingRequired]);









  /* Resuming a saved submission: land the customer directly on Additional
     Details and focus the first field still required. Presentation only. */
  const resumeScrolled = useRef(false);
  useEffect(() => {
    if (!resumed || resumeScrolled.current || typeof document === "undefined") return;
    if (!hasRequiredFields) return;
    resumeScrolled.current = true;
    const t = window.setTimeout(() => {
      document
        .querySelector('[data-testid="additional-details"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.setTimeout(() => emphasizeContextField(jumpTarget), 500);
    }, 400);
    return () => window.clearTimeout(t);
  }, [resumed, hasRequiredFields, jumpTarget]);

  return (
    <>
      <StepIndicator current={1} />
      <div className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-trust">
        {resumed ? "Saved submission" : "New submission"}
      </div>
      <h1 className="mt-2 font-display text-4xl text-navy md:text-5xl">
        {resumed ? "Continue Your Submission" : "Submit Your Invoice"}
      </h1>
      <p className="mt-3 max-w-2xl text-base text-muted-foreground md:text-lg">
        {resumed
          ? "We've already analyzed your uploaded invoice. Complete the remaining required information below to continue to payment."
          : "Upload your towing or storage invoice. In about 10–60 minutes after payment, you'll receive a professional findings package you can use to negotiate — or hand off to our specialists."}
      </p>

      {resumed && hasRequiredFields ? (
        <div
          data-testid="resume-status-card"
          className="mt-5 rounded-2xl border-2 border-trust/35 bg-trust/[0.05] px-5 py-4 shadow-card"
        >
          <p className="font-display text-lg font-extrabold text-navy">You're Almost Done</p>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            Your invoice has already been uploaded and analyzed. We only need the following required
            information before payment:
          </p>
          <ul className="mt-2 space-y-1">
            {requiredInOrder.map((f) => (
              <li key={f} className="text-sm font-semibold text-navy">
                • {REQUIRED_FIELD_LABEL[f] ?? f}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-sm text-muted-foreground">
            Complete these fields below to continue.
          </p>
        </div>
      ) : null}

      <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm text-navy/80">
        <li className="inline-flex items-center gap-1.5 rounded-full bg-savings-soft px-3 py-1">
          <Lock className="h-3.5 w-3.5 text-savings" /> Encrypted upload
        </li>
        <li className="inline-flex items-center gap-1.5 rounded-full bg-savings-soft px-3 py-1">
          <ShieldCheck className="h-3.5 w-3.5 text-savings" /> Private & secure
        </li>
        <li className="inline-flex items-center gap-1.5 rounded-full bg-trust-soft px-3 py-1 text-trust">
          <Clock className="h-3.5 w-3.5" /> Review begins after payment
        </li>
      </ul>

      {/* Method */}
      <div className="mt-7">
        <div className="mb-2.5 flex items-baseline justify-between">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Step 1 · Choose how to send your invoice
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <MethodCard
            active={method === "photo"}
            onClick={() => setMethod("photo")}
            icon={Camera}
            title="Take a Photo"
            body="Snap a quick photo of your invoice."
          />
          <MethodCard
            active={method === "upload"}
            onClick={() => setMethod("upload")}
            icon={Upload}
            title="Upload Files"
            body="PDF, JPG, PNG — up to 10 files."
          />
          <MethodCard
            active={method === "email"}
            onClick={() => setMethod("email")}
            icon={Mail}
            title="Email Invoice"
            body="Forward to your private submission email."
          />
        </div>
      </div>

      {/* Method body */}
      <div className="mt-4 rounded-2xl border border-border bg-card p-6 shadow-card">
        {method === "email" ? (
          <EmailIntake email={privateEmail} />
        ) : method === "photo" ? (
          <PhotoIntake files={files} setFiles={setFiles} onFiles={onFiles} />
        ) : (
          <UploadIntake files={files} setFiles={setFiles} onFiles={onFiles} />
        )}
      </div>

      {/* Optional claim information — customer context only. Everything the
          invoice itself can prove is extracted automatically by the pipeline. */}
      <div className="mt-5 space-y-2.5">
          {/* Ambient context only — deliberately the lightest element in the stack. */}
          <div className="rounded-xl border border-border/60 bg-secondary/35 px-4 py-2.5">
            <p className="text-xs leading-relaxed text-muted-foreground/85">
              <span className="font-semibold text-muted-foreground">Handled automatically:</span>{" "}
              invoice extraction, business, licensing, market and legal research. You only add what
              your invoice can't tell us.
            </p>
          </div>


          {/* Dismissible AI notifications — presentation only. */}
          <AiNotifications
            requiredFields={requiredFields}
            values={{
              vendor: details.vendor ?? "",
              zip: details.zip ?? "",
              vendorPhone: details.vendorPhone ?? "",
              vendorAddress: details.vendorAddress ?? "",
            }}
            isFilled={contextFilled}
            onGoToField={(f: NotificationFieldKey, o: { pulse: boolean }) =>
              emphasizeContextField(f, o.pulse)
            }
          />

          <div
            data-testid="additional-details"
            className="mt-2 rounded-2xl border border-border bg-card shadow-card scroll-mt-24"
          >
            <div className="px-5 py-5 sm:px-6 sm:py-5">
              <div className="mb-5">
                <h2 className="font-display text-lg font-extrabold tracking-tight text-navy">
                  Additional Details
                </h2>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground/70">
                  {requiredComplete
                    ? "Everything we need is in. Optional details below simply improve AI accuracy."
                    : "Only provide information that isn't already on your invoice. Required fields are highlighted. Optional fields simply improve AI accuracy."}
                </p>
              </div>

            {/* Service Provider Information — required identity fields first,
                recommended verification fields directly beneath them. These
                inputs are customer-entered only: no OCR / readiness value is
                ever written into them. */}
            <FormSection title="Service Provider Information">
              <div
                id="service-provider-fields"
                className="grid gap-4 sm:grid-cols-2"
              >
                {/* Required — these are what readiness actually gates on */}
                <div ref={vendorFieldRef} data-context-field="vendor">
                  <Field
                    label="Towing / Storage Company"
                    value={details.vendor ?? ""}
                    onChange={(v) => setDetails({ ...details, vendor: v })}
                    placeholder="Example: Flag City Towing and Recovery"
                    autoComplete="organization"
                    required={showValidation && needsVendor && !contextFilled("vendor")}
                    verified={needsVendor && contextFilled("vendor")}
                    requiredWhy="Needed for vendor identity, licensing and business registry research."
                    error={fieldError("vendor")}
                  />
                </div>
                <div ref={zipFieldRef} data-context-field="zip">
                  <Field
                    label="Service Location ZIP Code"
                    value={details.zip ?? ""}
                    onChange={(v) =>
                      setDetails({ ...details, zip: v.replace(/[^\d-]/g, "").slice(0, 10) })
                    }
                    placeholder="Example: 45840"
                    autoComplete="postal-code"
                    required={showValidation && needsZip && !contextFilled("zip")}
                    verified={needsZip && contextFilled("zip")}
                    requiredWhy="Needed for local market pricing and applicable state and local laws."
                    error={fieldError("zip")}
                    hint="City and State are detected automatically."
                  />
                </div>


                {/* Recommended — never blocks payment */}
                <div data-context-field="vendorPhone">
                  <Field
                    label="Business Phone Number"
                    type="tel"
                    value={details.vendorPhone ?? ""}
                    onChange={(v) => setDetails({ ...details, vendorPhone: v })}
                    placeholder="Example: (419) 555-1234"
                    autoComplete="tel"
                    recommended
                    recommendedActive={showValidation}
                  />
                </div>
                <div data-context-field="vendorAddress">
                  <Field
                    label="Business Address"
                    value={details.vendorAddress ?? ""}
                    onChange={(v) => setDetails({ ...details, vendorAddress: v })}
                    placeholder="Example: 123 Main Street"
                    autoComplete="street-address"
                    recommended
                    recommendedActive={showValidation}
                  />
                </div>
              </div>
            </FormSection>


            {/* Everything below rarely blocks payment — kept behind one
                disclosure so the page stays short. */}
            <MoreOptionalInformation>
              <SubsectionItem
                id="vehicle-subsection"
                hasValue={Boolean((details.vehicleDescription ?? "").trim() || (details.vehicleVin ?? "").trim())}
                icon={Car}
                title="Vehicle"
                helper="Year, make, and model."
              >
                <div className="grid gap-4">
                  <Field
                    label="Vehicle Year / Make / Model"
                    value={details.vehicleDescription}
                    onChange={(v) => setDetails({ ...details, vehicleDescription: v })}
                    placeholder="Example: 2021 Ford F-150"
                  />
                  <Field
                    label="Vehicle Identification Number (VIN)"
                    value={details.vehicleVin ?? ""}
                    onChange={(v) => {
                      setDetails({ ...details, vehicleVin: normalizeVin(v) });
                      setVinTouched(false);
                    }}
                    onBlur={() => setVinTouched(true)}
                    placeholder="Example: 1HGCM82633A004352"
                    hint="Enter the full 17-character VIN when available."
                    autoComplete="off"
                    error={vinValidationError(details.vehicleVin, vinTouched || attemptedRecheck) ?? undefined}
                  />
                </div>
              </SubsectionItem>

              <SubsectionItem
                id="insurance-subsection"
                hasValue={Boolean((details.insuranceCompany ?? "").trim() || (claimRef ?? "").trim() || (details.adjusterName ?? "").trim() || (details.adjusterEmail ?? "").trim())}
                icon={Shield}
                title="Insurance"
                helper="Used during Full-Service Negotiation if requested."
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Insurance Company"
                    value={details.insuranceCompany}
                    onChange={(v) => setDetails({ ...details, insuranceCompany: v })}
                    placeholder="Example: State Farm, GEICO, Progressive"
                    autoComplete="organization"
                  />
                  <Field
                    label="Claim Number"
                    value={claimRef}
                    onChange={setClaimRef}
                    placeholder="Example: 123456789"
                    autoComplete="off"
                  />
                  <Field
                    label="Adjuster Name"
                    value={details.adjusterName}
                    onChange={(v) => setDetails({ ...details, adjusterName: v })}
                    placeholder="Example: John Smith"
                    autoComplete="name"
                  />
                  <Field
                    label="Adjuster Email"
                    type="email"
                    value={details.adjusterEmail}
                    onChange={(v) => setDetails({ ...details, adjusterEmail: v })}
                    placeholder="john.smith@example.com"
                    autoComplete="email"
                  />
                </div>
              </SubsectionItem>

              <SubsectionItem
                id="storage-subsection"
                hasValue={Boolean((details.storageReason ?? "").trim())}
                icon={Package}
                title="Storage Context"
                helper="Helps explain why storage occurred."
              >
                <SelectField
                  label="Reason for Storage"
                  value={details.storageReason}
                  onChange={(v) => setDetails({ ...details, storageReason: v })}
                  options={STORAGE_REASONS}
                />
              </SubsectionItem>

              <SubsectionItem
                id="goals-subsection"
                hasValue={Boolean((details.reviewFocus ?? "").trim())}
                icon={Target}
                title="Review Goals"
                helper="Prioritizes AI findings."
              >
                <SelectField
                  label="What would you like us to focus on?"
                  value={details.reviewFocus}
                  onChange={(v) => setDetails({ ...details, reviewFocus: v })}
                  options={REVIEW_FOCUS_OPTIONS}
                />
              </SubsectionItem>

              <SubsectionItem
                id="comments-subsection"
                hasValue={Boolean((details.additionalInfo ?? "").trim())}
                icon={FileText}
                title="Additional Comments"
                helper="Anything else our AI should know."
              >
                <TextAreaField
                  label="Anything else our AI should know"
                  value={details.additionalInfo}
                  onChange={(v) => setDetails({ ...details, additionalInfo: v })}
                  placeholder={
                    "Tell us anything that may help with your review...\n\nExamples:\n• Insurance delayed inspection\n• Tow company refused release\n• Vehicle stored longer than expected\n• Duplicate storage charges"
                  }
                />
              </SubsectionItem>
            </MoreOptionalInformation>

            {/* Single customer-provided notice, immediately before the save
                action — the moment it matters to the customer. */}
            {hasRequiredFields && onSaveRequired ? (
              <div
                data-testid="customer-provided-notice"
                className="mt-4 rounded-xl border border-trust/25 bg-trust/[0.06] px-4 py-3"
              >
                <p className="text-xs font-semibold text-navy">
                  Your invoice remains the source of record.
                </p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  Information you enter is saved as Customer Provided and never replaces
                  information printed on your invoice.
                </p>
              </div>
            ) : null}

            {/* Save and Recheck lives at the very end of Optional Claim
                Information so the customer completes everything once and
                rechecks a single time. */}
            {hasRequiredFields && onSaveRequired ? (
              <div className="mt-5 rounded-2xl border border-border bg-secondary/30 px-6 py-2.5 animate-fade-in [animation-duration:280ms] motion-reduce:animate-none">
                {savingRequired ? (
                  <RecheckProgress />
                ) : (
                  <div className="flex flex-col items-center gap-3 text-center">
                    <Button
                      type="button"
                      data-testid="save-required-context"
                      onClick={() => {
                        /* Never a dead button: if something is still missing we
                           point the customer straight at it instead. */
                        if (unresolvedRequired.length > 0) {
                          setAttemptedRecheck(true);
                          emphasizeContextField(unresolvedRequired[0]!);
                          return;
                        }
                        setAttemptedRecheck(false);
                        void onSaveRequired({
                          vendor: needsVendor ? (details.vendor ?? "").trim() || undefined : undefined,
                          zip: needsZip ? (details.zip ?? "").trim() || undefined : undefined,
                          // Recommended values ride along whenever the customer
                          // typed them — they never gate the save.
                          vendorPhone: (details.vendorPhone ?? "").trim() || undefined,
                          vendorAddress: (details.vendorAddress ?? "").trim() || undefined,
                          vehicleVin: normalizeVin(details.vehicleVin) || undefined,
                        });
                      }}
                      className="min-h-[56px] w-full max-w-sm rounded-xl bg-gradient-to-b from-savings to-savings/90 px-10 text-base font-bold text-savings-foreground shadow-card transition-all duration-200 hover:-translate-y-[2px] hover:from-savings/95 hover:to-savings/80 hover:shadow-elevated active:translate-y-0 active:scale-[0.985]"
                    >
                      {requiredComplete ? (
                        <Check className="h-5 w-5" aria-hidden />
                      ) : (
                        <RefreshCw className="h-5 w-5" aria-hidden />
                      )}
                      {requiredComplete ? "Continue to Review" : "Save & Continue →"}

                    </Button>

                  </div>
                )}
              </div>
            ) : null}

          </div>
        </div>



        </div>




      {/* Once the claim exists this slot carries the payment / confirmation
          flow only — missing information lives in the AI notifications above. */}
      {confirmation ? (
        <section
          data-testid="submit-readiness-panel"
          className="mt-4 scroll-mt-24 animate-fade-in"
          aria-live="polite"
        >
          {savingRequired ? (
            <p
              data-testid="readiness-rechecking"
              className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-trust"
            >
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Checking Updated Information…
            </p>
          ) : null}
          {confirmation}
        </section>
      ) : (


      <div className="mt-7 rounded-2xl border border-border bg-gradient-to-br from-white to-secondary/40 p-5 shadow-card transition-shadow duration-200 hover:shadow-elevated sm:p-6">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Step 3 · Submit for review
        </div>
        <div className="flex flex-col-reverse items-stretch gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-xs text-muted-foreground sm:max-w-sm">
            <Lock className="h-3.5 w-3.5 shrink-0 text-savings" />
            <span>
              Your invoice is encrypted and stored privately in your account. Payment happens on the
              next screen.
            </span>
          </div>
          <button
            disabled={!canSubmit || submitting}
            onClick={submit}
            aria-busy={submitting}
            className="group inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-savings to-savings/90 px-7 py-3.5 text-sm font-bold text-savings-foreground shadow-elevated transition-all duration-200 hover:-translate-y-0.5 hover:from-savings/95 hover:to-savings/85 hover:shadow-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trust focus-visible:ring-offset-2 active:translate-y-0 active:scale-[0.985] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-card disabled:hover:translate-y-0"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" />
                {activeStage ? stageDef(activeStage).buttonLabel : "Preparing…"}
              </>
            ) : stage === "done" ? (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Review Prepared
              </>
            ) : (
              <>
                {submitError ? "Try Again" : "Continue to Review"}
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </>
            )}
          </button>
        </div>
        {(submitting || submitError || stage === "done") && (
          <SubmissionProgress
            activeStage={activeStage}
            finished={stage === "done"}
            elapsedMs={elapsedMs}
            stalled={stalled}
            error={submitError}
            onRetry={submit}
            onSaveForLater={onSaveForLater}
          />
        )}
      </div>
      )}

    </>
  );
}

/* ---------------- Missing-field guidance ---------------- */

/** Page order of the customer-enterable context fields. */
const CONTEXT_FIELD_ORDER = ["vendor", "vendorPhone", "vendorAddress", "zip"] as const;

/** Customer-facing names for the required context fields. */
const REQUIRED_FIELD_LABEL: Record<string, string> = {
  vendor: "Towing / Storage Company",
  vendorPhone: "Business Phone",
  vendorAddress: "Business Address",
  zip: "Service Location ZIP Code",
};

/** Success wording shown once a required context field has been supplied. */

/**
 * Scroll to, focus and briefly emphasize a context field. Presentation only —
 * it never changes any value or readiness result.
 */
function emphasizeContextField(field: string, pulse = true) {
  if (typeof document === "undefined") return;
  const wrapper = document.querySelector<HTMLElement>(`[data-context-field="${field}"]`);
  const input = wrapper?.querySelector("input");
  if (!wrapper || !input) return;
  const rect = input.getBoundingClientRect();
  const visible = rect.top >= 0 && rect.bottom <= window.innerHeight;
  if (!visible) input.scrollIntoView({ behavior: "smooth", block: "center" });
  window.setTimeout(
    () => {
      input.focus({ preventScroll: true });
      if (!pulse) return;
      const box = wrapper.querySelector<HTMLElement>("[data-required-field]") ?? wrapper;
      box.classList.add("field-flash");
      window.setTimeout(() => box.classList.remove("field-flash"), 1200);
    },
    visible ? 0 : 350,
  );
}




/* ---------------- Recheck progress ---------------- */

const RECHECK_STEPS = [
  "Rechecking invoice",
  "Vendor verified",
  "Service location confirmed",
  "AI analysis updated",
];


/**
 * Compact, presentation-only progress sequence shown while the real recheck
 * request is in flight. It reflects the work already happening server-side —
 * no step here triggers anything.
 */
function RecheckProgress() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = window.setInterval(
      () => setStep((s) => Math.min(s + 1, RECHECK_STEPS.length)),
      450,
    );
    return () => window.clearInterval(t);
  }, []);
  return (
    <div data-testid="recheck-progress" aria-live="polite">
      <p className="inline-flex items-center gap-2 text-sm font-semibold text-navy">
        <Loader2 className="h-4 w-4 animate-spin text-trust" aria-hidden />
        Verifying your invoice…
      </p>
      <ul className="mt-3 space-y-1.5 text-sm">

      {RECHECK_STEPS.map((label, i) => {
        const done = i < step;
        const active = i === step;
        if (!done && !active) return null;
        return (
          <li
            key={label}
            className={`flex items-center gap-2 animate-fade-in ${
              done ? "text-savings" : "text-muted-foreground"
            }`}
          >
            {done ? (
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
            ) : (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
            )}
            {label}
            {done ? "" : "…"}
          </li>
        );
      })}
      </ul>
      {step >= RECHECK_STEPS.length ? (
        <p className="mt-3 text-sm font-semibold text-savings animate-fade-in">Analysis Updated</p>
      ) : null}
    </div>
  );
}


/* ---------------- Intake variants ---------------- */


function UploadIntake({
  files,
  setFiles,
  onFiles,
}: {
  files: File[];
  setFiles: (f: File[]) => void;
  onFiles: (l: FileList | null) => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  return (
    <>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          onFiles(e.dataTransfer.files);
        }}
        className={`flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-12 text-center transition-all duration-200 cursor-pointer focus-within:border-trust focus-within:bg-trust-soft/40 ${
          dragOver
            ? "border-trust bg-trust-soft scale-[1.01]"
            : "border-border hover:border-trust/50 hover:bg-trust-soft/30"
        }`}
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-trust-soft text-trust">
          <Upload className="h-7 w-7" />
        </div>
        <div className="mt-4 text-base font-semibold text-navy">
          Drag your invoice here, or click to browse
        </div>
        <div className="mt-1.5 text-xs text-muted-foreground">
          Up to 10 files · 20 MB each · encrypted end-to-end
        </div>
        <div className="mt-3 flex flex-wrap justify-center gap-1.5">
          {["PDF", "JPG", "PNG", "HEIC", "WEBP"].map((ext) => (
            <span
              key={ext}
              className="rounded-md bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
            >
              {ext}
            </span>
          ))}
        </div>
        <input
          type="file"
          className="sr-only"
          multiple
          accept="image/*,application/pdf"
          onChange={(e) => onFiles(e.target.files)}
          aria-label="Choose invoice files to upload"
        />
      </label>
      <FileList files={files} onRemove={(i) => setFiles(files.filter((_, idx) => idx !== i))} />
    </>
  );
}


function PhotoIntake({
  files,
  setFiles,
  onFiles,
}: {
  files: File[];
  setFiles: (f: File[]) => void;
  onFiles: (l: FileList | null) => void;
}) {
  return (
    <>
      <label className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border p-10 text-center hover:bg-secondary">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-trust-soft text-trust">
          <Camera className="h-7 w-7" />
        </div>
        <div className="mt-4 text-sm font-semibold text-navy">Tap to open your camera</div>
        <div className="mt-1 text-xs text-muted-foreground">
          Snap one or more photos of your invoice
        </div>
        <span className="mt-4 inline-flex items-center gap-2 rounded-lg bg-trust px-4 py-2 text-sm font-semibold text-trust-foreground">
          <Camera className="h-4 w-4" /> Take Photo
        </span>
        <input
          type="file"
          className="hidden"
          multiple
          accept="image/*"
          capture="environment"
          onChange={(e) => onFiles(e.target.files)}
        />
      </label>
      <FileList files={files} onRemove={(i) => setFiles(files.filter((_, idx) => idx !== i))} />
    </>
  );
}

function EmailIntake({ email }: { email: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    if (typeof navigator === "undefined") return;
    navigator.clipboard?.writeText(email).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  }
  return (
    <div>
      <div className="text-sm text-muted-foreground">
        Forward invoices to your private submission email:
      </div>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex flex-1 items-center gap-3 rounded-lg border border-dashed border-trust/30 bg-trust-soft p-4 font-mono text-sm text-trust">
          <Mail className="h-4 w-4 shrink-0" />
          <span className="truncate">{email}</span>
        </div>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-white px-4 py-3 text-sm font-semibold text-navy hover:bg-secondary"
        >
          {copied ? <Check className="h-4 w-4 text-savings" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <p className="mt-3 text-sm text-muted-foreground">
        We'll create a case automatically when the email arrives. You can add details anytime.
      </p>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function FileList({ files, onRemove }: { files: File[]; onRemove: (i: number) => void }) {
  if (files.length === 0) return null;
  return (
    <ul className="mt-4 space-y-2" aria-label="Selected files">
      {files.map((f, i) => {
        const isImage = f.type.startsWith("image/");
        const preview = isImage ? URL.createObjectURL(f) : null;
        return (
          <li
            key={`${f.name}-${f.size}-${i}`}
            className="flex items-center justify-between gap-3 rounded-lg border border-border bg-white p-3 text-sm animate-fade-in"
          >
            <div className="flex min-w-0 items-center gap-3">
              {preview ? (
                <img
                  src={preview}
                  alt=""
                  className="h-9 w-9 shrink-0 rounded-md object-cover ring-1 ring-border"
                  onLoad={() => URL.revokeObjectURL(preview)}
                />
              ) : (
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-trust-soft text-trust">
                  <FileText className="h-4 w-4" />
                </div>
              )}
              <div className="min-w-0">
                <div className="truncate font-medium text-navy">{f.name}</div>
                <div className="text-xs text-muted-foreground">{formatFileSize(f.size)}</div>
              </div>
            </div>
            <button
              onClick={() => onRemove(i)}
              className="shrink-0 rounded-md p-1.5 text-muted-foreground transition hover:bg-secondary hover:text-navy"
              aria-label={`Remove ${f.name}`}
            >
              <X className="h-4 w-4" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}


/* ---------------- Confirmation ---------------- */

export function ConfirmationState({
  c,
  onAnother,
  mode = "live",
  onReadinessState,
}: {
  c: Case;
  onAnother: () => void;
  /** Explicit routing mode carried from the duplicate dialog. */
  mode?: SubmissionMode;
  /**
   * Reports the still-unresolved customer-enterable requirements plus the save
   * action, so the EXISTING Optional Claim Information form can highlight and
   * resolve them in place instead of a second form being built here.
   */
  onReadinessState?: (s: {
    fields: string[];
    save: (v: ReadinessAnswers) => Promise<void>;
    /** The readiness / extraction evaluation has finished for this claim. */
    attempted?: boolean;
  }) => void;


}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selected, setSelected] = useState<"review" | "negotiation">("review");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  // Testing-only: skips the STOP, never the readiness evaluation or warnings.
  const [testingOverride, setTestingOverride] = useState(false);
  const payInFlight = useRef(false);


  /* Pre-payment gate — service selection and checkout stay hidden until the
     server confirms the uploaded invoice carries what a reliable review needs.
     The AI notifications above share this query key so both read one cached
     result. */
  const fetchReadiness = useServerFn(getClaimReadiness);
  const queryClient = useQueryClient();
  const {
    data: reviewRow,
    isPending: reviewPending,
    isError: reviewIsError,
    refetch: refetchReview,
  } = useQuery({
    // The lookup is RLS-scoped: without a hydrated session it returns
    // {data:null,error:null}, which used to cache a permanent "no claim" and
    // strand readiness. Wait for the session, and treat a missing row as a
    // retryable condition rather than a settled answer.
    enabled: !!user?.id,
    queryKey: ["review-id-by-file-number", c.id, user?.id ?? null],
    retry: 3,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("id")
        .eq("file_number", c.id)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error("claim-not-visible-yet");
      return data;
    },
  });
  const reviewId = reviewRow?.id ?? null;


  const {
    data: readiness,
    isPending: readinessPending,
    isError: readinessIsError,
    refetch: refetchReadiness,
  } = useQuery({
    enabled: !!reviewId,
    queryKey: ["claim-readiness", reviewId],
    retry: 1,
    // The request itself is bounded: a hanging server call must still settle
    // the query so the screen can render an explicit state.
    queryFn: async () => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        return await Promise.race([
          fetchReadiness({ data: { reviewId: reviewId as string } }),
          new Promise<never>((_, reject) => {
            timer = setTimeout(
              () => reject(new Error("readiness-request-timeout")),
              READINESS_STALL_MS,
            );
          }),
        ]);
      } finally {
        if (timer) clearTimeout(timer);
      }
    },
  });

  // Bounded stall protection — an unresolved check never spins forever.
  // The deadline is anchored to the first time THIS claim was observed, so a
  // remount, refetch or re-render can never extend the checking state.
  const [timedOut, setTimedOut] = useState(false);
  const settled = !reviewPending && (!reviewId || !readinessPending);
  const startedAt = useRef(Date.now());
  useEffect(() => {
    if (settled) return;
    const t = setTimeout(() => setTimedOut(true), readinessMsRemaining(c.id));
    return () => clearTimeout(t);
  }, [settled, c.id]);

  // One decision for the whole screen: readiness always runs before any
  // success, service-selection or payment UI, in BOTH modes.
  const readinessView: ReadinessView = resolveReadinessView({
    reviewPending,
    reviewError: reviewIsError,
    reviewId,
    readinessPending: !!reviewId && readinessPending,
    readinessError: readinessIsError,
    readinessStatus: readiness?.status ?? null,
    timedOut,
  });

  useEffect(() => {
    if (readinessView !== "unavailable") return;
    // PII-safe diagnostic only.
    console.warn("[readiness] unavailable", {
      fileNumber: c.id,
      reviewId,
      mode,
      stage: reviewIsError || !reviewId ? "review_lookup" : timedOut ? "stalled" : "readiness",
      elapsedMs: Date.now() - startedAt.current,
      timestamp: new Date().toISOString(),
    });
  }, [readinessView, c.id, mode, reviewIsError, reviewId, timedOut]);

  const retryReadiness = async () => {
    // Retry reruns ONLY the lookup/readiness requests — never a new review.
    resetReadinessDeadline(c.id);
    startedAt.current = Date.now();
    setTimedOut(false);
    const r = await refetchReview();
    if (r.data?.id) await refetchReadiness();
  };


  const view = decideConfirmationView({ mode, readiness: readinessView, testingOverride });

  /* Bridge the customer-enterable requirements up to the existing form. */
  const saveAnswers = useServerFn(saveReadinessAnswers);
  const requiredFields = useMemo(
    () => customerEnterableFields(readiness, view.showRequirements),
    [view.showRequirements, readiness],
  );

  const saveRequired = useCallback(
    async (v: ReadinessAnswers) => {
      if (!reviewId) return;
      await saveAnswers({
        data: {
          reviewId,
          vendor: v.vendor,
          zip: v.zip,
          vendorPhone: v.vendorPhone,
          vendorAddress: v.vendorAddress,
          vendorCity: v.vendorCity,
          vendorState: v.vendorState,
          vehicleVin: v.vehicleVin,
        },
      });
      await refetchReadiness();
    },
    [reviewId, saveAnswers, refetchReadiness],
  );
  /* The extraction has genuinely been attempted only once the readiness
     evaluation for this claim has settled with a real answer. Until then the
     form stays neutral — never red. */
  const extractionAttempted = !!reviewId && !readinessPending && !!readiness;
  useEffect(() => {
    onReadinessState?.({
      fields: requiredFields,
      save: saveRequired,
      attempted: extractionAttempted,
    });
  }, [onReadinessState, requiredFields, saveRequired, extractionAttempted]);



  /* Presentation-only: once blocking requirements existed and the claim later
     resolves to ready, confirm to the customer that their input mattered. */
  const hadRequirements = useRef(false);
  const [verifiedVia, setVerifiedVia] = useState<string[] | null>(null);
  useEffect(() => {
    if (!readiness) return;
    if (readiness.status === "incomplete") {
      hadRequirements.current = true;
      return;
    }
    if (readiness.status !== "ready" || !hadRequirements.current) return;
    const p = readiness.provided;
    const matched: string[] = [];
    if (p.vendor) matched.push("Business Name");
    if (p.zip) matched.push("ZIP Code");
    if (p.vendorPhone) matched.push("Business Phone");
    if (p.vendorAddress) matched.push("Business Address");
    setVerifiedVia(matched.length ? matched : ["Invoice information"]);
  }, [readiness]);

  /* Missing information is now surfaced as dismissible AI notifications above
     the form — no separate readiness section. */







  const uploadedAt = new Date(c.createdAt);
  const uploadTime = uploadedAt.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
  const invoiceDate = c.details.invoiceDate
    ? new Date(c.details.invoiceDate).toLocaleDateString(undefined, { dateStyle: "medium" })
    : "To be confirmed";
  const vendor = c.details.vendor || "To be confirmed";

  async function onPay() {
    if (payInFlight.current) return;
    if (!user) return;
    if (!consent) {
      toast.error("Please review and accept our terms before continuing.");
      return;
    }
    payInFlight.current = true;
    setLoading(true);
    try {
      const { stripeService } = await import("@/lib/services/stripeService");
      const { data: review } = await supabase
        .from("reviews")
        .select("id")
        .eq("file_number", c.id)
        .maybeSingle();
      const res = await stripeService.startCheckout({
        kind: selected === "negotiation" ? "negotiation_fee" : "review_fee",
        reviewId: review?.id,
      });
      const url = new URL(res.url, window.location.origin);
      url.searchParams.set("review", c.id);
      window.location.href = url.toString();
    } catch (e) {
      console.error(e);
      const message = e instanceof Error ? e.message : "";
      if (message.includes("DOCUMENTS_INCOMPLETE")) {
        // The server is the boundary — refresh the gate so the customer sees
        // exactly what is still missing instead of a generic failure.
        await queryClient.invalidateQueries({ queryKey: ["claim-readiness", reviewId] });
        toast.error("We need a little more information about this invoice before payment.");
      } else {
        toast.error("We couldn't start checkout. Please try again.");
      }
    } finally {
      setLoading(false);
      payInFlight.current = false;
    }
  }

  return (
    <div>
      {view.evaluating ? (
        <div
          data-testid="readiness-checking"
          className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-10 text-center text-sm text-muted-foreground shadow-card"
        >
          <Loader2 className="h-5 w-5 animate-spin text-trust" aria-hidden />
          <span className="font-semibold text-navy">Checking Your Invoice</span>
          <span className="max-w-md">{READINESS_CHECKING_BODY}</span>
        </div>
      ) : null}

      {readinessView === "unavailable" ? (
        <section
          role="alert"
          data-testid="readiness-unavailable"
          className="rounded-2xl border border-destructive/30 bg-destructive/5 p-8 text-center shadow-card"
        >
          <h2 className="font-display text-2xl text-navy">{READINESS_UNAVAILABLE_TITLE}</h2>
          <p className="mx-auto mt-3 max-w-lg text-sm text-muted-foreground">
            {READINESS_UNAVAILABLE_BODY}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button type="button" className="min-h-11" onClick={() => void retryReadiness()}>
              Try Again
            </Button>
            <Button type="button" variant="outline" className="min-h-11" onClick={onAnother}>
              Return to Submit
            </Button>
            <Button asChild type="button" variant="outline" className="min-h-11">
              <Link to="/support">Contact Support</Link>
            </Button>
          </div>
        </section>
      ) : null}


      {view.showSuccessHero ? (
        <>
          {/* Success hero */}
          <div className="flex flex-col items-center text-center">
            <div className="relative flex h-24 w-24 items-center justify-center animate-scale-in">
              <span className="absolute inset-0 rounded-full bg-savings-soft" />
              <span className="absolute inset-2 rounded-full bg-savings/15" />
              <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-savings text-white shadow-elevated">
                <CheckCircle2 className="h-10 w-10" strokeWidth={2.25} />
              </span>
            </div>
            {/* h2, not h1: the page already carries "Submit Your Invoice" as
                its single H1 and the form stays mounted above this panel. */}
            <h2 className="mt-8 font-display text-4xl text-navy md:text-5xl">
              Invoice Information Complete
            </h2>

            <p className="mt-4 max-w-xl text-muted-foreground">
              We have enough information to begin your professional review.
            </p>
          </div>

          {/* Invoice summary */}
          <div className="mt-7 rounded-2xl border border-border bg-card p-8 shadow-card">
            <div className="grid gap-6 sm:grid-cols-2">
              <SummaryRow
                label="Invoice Number"
                value={<span className="font-mono text-navy">{c.id}</span>}
              />
              <SummaryRow label="Vendor" value={<span className="text-navy">{vendor}</span>} />
              <SummaryRow
                label="Invoice Date"
                value={<span className="text-navy">{invoiceDate}</span>}
              />
              <SummaryRow
                label="Upload Time"
                value={<span className="text-navy">{uploadTime}</span>}
              />
              <SummaryRow
                label="Estimated Processing Time"
                value={
                  <span className="inline-flex items-center gap-1.5 text-navy">
                    <Clock className="h-4 w-4 text-trust" /> ~10–60 minutes after payment
                  </span>
                }
              />
              <SummaryRow label="Submission Method" value={METHOD_LABEL[c.method]} />
            </div>
          </div>

          {/* Next-step transition */}
          <div className="mt-7 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <div className="text-center">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Next Step
              </div>
              <div className="text-sm font-medium text-navy">
                Choose how you'd like your invoice reviewed.
              </div>
            </div>
            <div className="h-px flex-1 bg-border" />
          </div>
        </>
      ) : null}

      {reviewId && view.showRequirements && view.showTestingContinue ? (
        <div className="mt-6 rounded-xl border border-dashed border-trust/50 bg-trust/5 p-5">
          <p className="text-sm font-semibold text-navy">Authorized testing submission</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Validation still runs exactly as it does for a live customer. Testing accounts may
            continue past the stop; server-side payment protection still applies.
          </p>
          <Button
            type="button"
            variant="outline"
            data-testid="readiness-testing-continue"
            onClick={() => setTestingOverride(true)}
            className="mt-4 min-h-11"
          >
            Continue anyway (testing)
          </Button>
        </div>
      ) : null}
      {reviewId && view.showRequirements ? null : verifiedVia ? (

        <section
          data-testid="readiness-verified-banner"
          className="mt-7 rounded-2xl border border-savings/25 bg-savings-soft/50 p-6 shadow-card animate-fade-in sm:p-8"
        >
          <div className="flex items-start gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-savings/15 text-savings animate-scale-in">
              <CheckCircle2 className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="font-display text-2xl text-navy">AI Invoice Analysis Complete</h2>
              <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                Everything needed for your Professional AI Review has been successfully verified.
              </p>
              <ul className="mt-5 grid gap-2.5 text-sm text-navy sm:grid-cols-2">
                {[
                  "Business verified",
                  "Service location verified",
                  "Invoice ready",
                ].map((v) => (
                  <li key={v} className="flex items-center gap-2.5 animate-fade-in">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-savings/15 text-savings">
                      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                    </span>
                    {v}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm font-semibold text-savings">
                Continue to Payment →
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                Matched using {verifiedVia.join(", ")}.
              </p>
            </div>
          </div>
        </section>
      ) : null}



      {!reviewId || !view.showServiceSelection ? null : (
      <>

      {view.disclosure === READY_DISCLOSURE ? (
        /* The success hero above already states this verbatim — never render
           the same confirmation twice. */
        view.showSuccessHero ? null : (
          <div className="mt-6 flex items-center justify-center gap-2 rounded-xl border border-savings/30 bg-savings-soft px-4 py-2.5 text-sm font-semibold text-savings">
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            {READY_DISCLOSURE}
          </div>
        )
      ) : (
        <div className="mt-6 flex flex-col items-center gap-1 rounded-xl border border-dashed border-trust/40 bg-trust/5 px-4 py-2.5 text-center text-sm font-semibold text-trust">
          <span>{TESTING_CONTINUE_DISCLOSURE}</span>
          <span className="text-xs font-normal text-muted-foreground">
            Payment is not bypassed — this testing submission uses the normal checkout.
          </span>
        </div>
      )}



      {/* Choose service */}

      <div className="mt-8 text-center">
        <h2 className="font-display text-3xl text-navy md:text-4xl">
          Choose Your Review
        </h2>
        <p className="mt-2 text-sm text-muted-foreground md:text-base">
          Select the level of assistance that's right for your situation.
        </p>
      </div>

      <div className="mt-5 grid items-stretch gap-4 md:grid-cols-2">
        <ServiceCard
          selected={selected === "review"}
          onSelect={() => setSelected("review")}
          title="Professional AI Review"
          priceLabel={REVIEW_PRICE_LABEL}
          subtitle="You negotiate using our professional findings."
          tone="trust"
          steps={[
            { n: 1, text: "AI reviews your invoice." },
            { n: 2, text: "We compare pricing, regulations, and market data." },
            { n: 3, text: "Receive your Professional Findings Report." },
            { n: 4, text: "Negotiate with confidence using our recommendations." },
          ]}
          features={[
            "AI Invoice Review",
            "Professional Findings Report",
            "Market Comparison",
            "State Law & Regulation Review",
            "Timeline Analysis",
            "Charge Validation",
            "Downloadable PDF Report",
            "Live Review Portal",
            "Self-Negotiation Guidance",
          ]}
          footnote="Best for customers who want to negotiate themselves using our professional findings."
        />
        <ServiceCard
          selected={selected === "negotiation"}
          onSelect={() => setSelected("negotiation")}
          title="Full-Service Negotiation"
          priceLabel={NEGOTIATION_PRICE_LABEL}
          subtitle="We negotiate on your behalf."
          subtitleNote="Includes EVERYTHING in Professional AI Review"
          tone="negotiate"
          priceNote={
            <>
              <p className="font-semibold text-navy">
                {NEGOTIATION_TYPICAL_RANGE_LABEL}
              </p>
              <p className="mt-2 leading-relaxed text-muted-foreground">
                {NEGOTIATION_PRICING_ESTIMATE_DISCLAIMER}
              </p>
            </>
          }
          badge="Done-For-You Service"
          hero
          trustIndicator="Handled by Experienced Specialists"
          steps={[
            { n: 1, text: "Upload your invoice." },
            {
              n: 2,
              text:
                "You're done. Our specialists take over — we verify claim information, review the invoice, negotiate with the vendor, keep you updated, and deliver your final settlement package.",
            },
          ]}
          featuresIntro="Plus"
          features={[
            "Dedicated Negotiation Specialist",
            "We negotiate directly with the storage company",
            "Personalized negotiation strategy",
            "Progress updates throughout your case",
            "Final settlement summary",
            "No need to negotiate yourself",
          ]}
          footnote="Sit back while our specialists negotiate on your behalf."
        />
      </div>


      {/* Shared-features strip */}
      <div className="mt-4 border-y border-border bg-secondary/30 py-2.5 px-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-navy/85">
          <span className="inline-flex items-center gap-1.5 font-semibold text-navy">
            <CheckCircle2 className="h-3.5 w-3.5 text-savings" aria-hidden />
            Every review includes
          </span>
          {[
            "AI Invoice Review",
            "Professional Findings Report",
            "Market Comparison",
            "State Law Review",
            "Timeline Analysis",
            "Downloadable PDF",
            "Live Review Portal",
          ].map((f) => (
            <span key={f} className="inline-flex items-center gap-1.5 text-xs">
              <span className="h-1 w-1 rounded-full bg-navy/40" aria-hidden />
              {f}
            </span>
          ))}
        </div>
      </div>

      {/* Trust notification */}
      <div className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-savings/20 bg-savings-soft/70 px-3 py-2 text-sm font-medium text-savings">
        <CheckCircle2 className="h-4 w-4" aria-hidden />
        Your invoice is safely stored.
        <span className="text-savings/80">Return anytime to complete payment.</span>
      </div>

      {/* Order Summary + Actions */}
      <div className="mt-4 rounded-2xl border border-border bg-card p-7 shadow-elevated">
        <div className="flex items-center justify-between gap-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Order Summary
          </div>
          <CreditCard className="h-5 w-5 text-trust" />
        </div>
        <dl className="mt-4 space-y-2 text-sm">
          <div className="flex items-baseline justify-between gap-4 border-b border-dashed border-border pb-2">
            <dt className="text-navy/85">
              {selected === "negotiation" ? "Full-Service Negotiation" : "Professional AI Review"}
            </dt>
            <dd className="font-semibold text-navy">
              {selected === "negotiation" ? NEGOTIATION_PRICE_LABEL : REVIEW_PRICE_LABEL}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-base font-semibold text-navy">Total Today</dt>
            <dd className="text-lg font-bold text-savings">
              {selected === "negotiation" ? NEGOTIATION_PRICE_LABEL : REVIEW_PRICE_LABEL}
            </dd>
          </div>
        </dl>
        <LegalConsent id={`consent-${c.id}`} checked={consent} onChange={setConsent} className="mt-4 p-2" />
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={onPay}
            disabled={loading || !consent}
            className="group inline-flex flex-[1.5] items-center justify-center gap-2 rounded-xl bg-savings px-8 py-4 text-base font-semibold text-savings-foreground shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:shadow-elevated disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Redirecting to Secure Checkout...
              </>
            ) : (
              <>
                <CreditCard className="h-5 w-5" />
                Start My Review
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate({ to: "/dashboard", search: {} })}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-white px-5 py-3 text-sm font-semibold text-navy hover:bg-secondary"
          >
            Save & Return Later
          </button>
        </div>
        <p className="mt-4 text-center text-xs text-trust/80">
          <span className="mr-1" aria-hidden>🔒</span>
          Your invoice is encrypted, securely stored, and ready whenever you're ready to continue.
        </p>
      </div>
      </>
      )}


      <div className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-center text-xs">
        <button
          onClick={onAnother}
          className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-wider text-muted-foreground hover:text-navy"
        >
          <Plus className="h-3.5 w-3.5" /> Submit another invoice
        </button>
        <span aria-hidden className="h-1 w-1 rounded-full bg-border" />
        <Link
          to="/dashboard"
          search={{}}
          className="font-semibold uppercase tracking-wider text-muted-foreground hover:text-navy"
        >
          Go to my cases
        </Link>
      </div>

    </div>
  );
}

function ServiceCard({
  selected,
  onSelect,
  title,
  priceLabel,
  priceNote,
  subtitle,
  subtitleNote,
  tone,
  badge,
  hero,
  trustIndicator,
  steps,
  features,
  featuresIntro,
  footnote,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  priceLabel: string;
  priceNote?: React.ReactNode;
  subtitle: string;
  subtitleNote?: string;
  tone: "trust" | "negotiate";
  badge?: string;
  hero?: boolean;
  trustIndicator?: string;
  steps: { n: number; text: string }[];
  features: string[];
  featuresIntro?: string;
  footnote?: string;
}) {
  const accent =
    tone === "negotiate"
      ? {
          ring: selected
            ? "border-negotiate bg-gradient-to-b from-negotiate/[0.08] to-negotiate/[0.02] shadow-elevated"
            : "border-negotiate/40 bg-gradient-to-b from-negotiate/[0.04] to-transparent hover:border-negotiate hover:shadow-elevated",
          bubble: selected ? "border-negotiate bg-negotiate text-white" : "border-negotiate/40 bg-white",
          step: "bg-negotiate/10 text-negotiate",
          badge: "bg-negotiate text-negotiate-foreground",
          tick: "text-negotiate",
        }
      : {
          ring: selected
            ? "border-trust bg-trust-soft shadow-elevated"
            : "border-border bg-card hover:border-trust/50 hover:shadow-card",
          bubble: selected ? "border-trust bg-trust text-white" : "border-border bg-white",
          step: "bg-trust/10 text-trust",
          badge: "bg-trust text-trust-foreground",
          tick: "text-savings",
        };
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`group relative flex h-full flex-col rounded-2xl text-left transition-all duration-200 hover:-translate-y-0.5 ${
        hero
          ? "border-2 p-8 md:p-10 md:min-h-[640px] ring-1 ring-negotiate/10"
          : "border-2 p-7"
      } ${accent.ring} ${hero && !selected ? "shadow-card" : ""}`}
    >
      {badge && (
        <span
          className={`absolute -top-4 left-1/2 -translate-x-1/2 rounded-full shadow-elevated ${
            hero ? "px-4 py-1.5 text-[11px]" : "px-3 py-1 text-[10px]"
          } font-bold uppercase tracking-wider ${accent.badge}`}
        >
          {badge}
        </span>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className={`font-display text-navy ${hero ? "text-3xl" : "text-2xl"}`}>
            {title}
          </div>
          <div className={`mt-1.5 font-semibold text-navy/90 ${hero ? "text-base" : "text-sm"}`}>
            {subtitle}
          </div>
          {subtitleNote && (
            <div className="mt-1.5 text-xs font-semibold uppercase tracking-wider text-negotiate">
              {subtitleNote}
            </div>
          )}
        </div>
        <div
          className={`mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition ${accent.bubble}`}
        >
          {selected && <CheckCircle2 className="h-4 w-4" />}
        </div>
      </div>
      <div className="mt-5">
        {priceLabel.startsWith("$") ? (
          <div className="flex items-baseline gap-1.5">
            <span className={`font-semibold text-navy ${hero ? "text-5xl" : "text-4xl"}`}>
              {priceLabel}
            </span>
            <span className="text-sm text-muted-foreground">one-time</span>
          </div>
        ) : (
          <div className="inline-flex w-fit items-center rounded-lg border border-border bg-white/70 px-3 py-1.5 text-sm font-semibold text-navy">
            {priceLabel}
          </div>
        )}
        {priceNote && (
          <div className="mt-3 text-sm">
            {typeof priceNote === "string" ? (
              <p className="leading-relaxed text-muted-foreground">{priceNote}</p>
            ) : (
              priceNote
            )}
          </div>
        )}
      </div>


      {trustIndicator && (
        <div
          data-slot="trust-indicator"
          className="mt-4 inline-flex w-fit items-center gap-2 rounded-full border border-negotiate/25 bg-white/70 px-3 py-1.5 text-xs font-semibold text-negotiate"
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          <span>{trustIndicator}</span>
        </div>
      )}

      <div className="mt-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          How it works
        </div>
        <ol className="mt-3 space-y-2.5">
          {steps.map((s) => (
            <li key={s.n} className="flex items-start gap-3">
              <span
                className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${accent.step}`}
              >
                {s.n}
              </span>
              <span className="text-sm text-navy/85">{s.text}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-6">
        {featuresIntro && (
          <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {featuresIntro}
          </div>
        )}
        <ul className="space-y-2.5">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-navy/85">
              <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${accent.tick}`} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      {footnote && (
        <div
          className={`mt-6 rounded-xl border px-4 py-3 text-sm font-semibold ${
            tone === "negotiate"
              ? "border-negotiate/30 bg-negotiate/5 text-negotiate"
              : "border-trust/30 bg-trust-soft text-trust"
          }`}
        >
          {footnote}
        </div>
      )}
    </button>
  );
}


function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-1.5 text-sm">{value}</div>
    </div>
  );
}

/* ---------------- Small UI helpers ---------------- */

function MethodCard({
  active,
  onClick,
  icon: Icon,
  title,
  body,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`group relative flex h-full flex-col items-start gap-4 rounded-2xl border-2 p-6 text-left transition-all duration-200 hover:-translate-y-0.5 ${
        active
          ? "border-trust bg-trust-soft shadow-elevated"
          : "border-border bg-card hover:border-trust/40 hover:shadow-card"
      }`}
    >
      {active && (
        <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-trust text-trust-foreground shadow-card">
          <CheckCircle2 className="h-4 w-4" />
        </span>
      )}
      <div
        className={`flex h-12 w-12 items-center justify-center rounded-xl transition ${
          active ? "bg-trust text-trust-foreground" : "bg-secondary text-navy group-hover:bg-trust-soft group-hover:text-trust"
        }`}
      >
        <Icon className="h-6 w-6" />
      </div>
      <div>
        <div className="text-base font-semibold text-navy">{title}</div>
        <div className="mt-1 text-sm text-muted-foreground">{body}</div>
      </div>
    </button>
  );
}

/* ---------------- Step indicator ---------------- */

function StepIndicator({ current }: { current: 1 | 2 | 3 }) {
  const steps = [
    { n: 1, label: "Upload" },
    { n: 2, label: "Details" },
    { n: 3, label: "Payment" },
  ] as const;
  return (
    <ol className="flex items-center gap-2" aria-label="Submission progress">
      {steps.map((s, i) => {
        const isCurrent = s.n === current;
        const isDone = s.n < current;
        return (
          <li key={s.n} className="flex items-center gap-2">
            <div
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                isCurrent
                  ? "bg-trust text-trust-foreground shadow-card"
                  : isDone
                    ? "bg-savings-soft text-savings"
                    : "bg-secondary text-muted-foreground"
              }`}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                  isCurrent
                    ? "bg-white/20 text-white"
                    : isDone
                      ? "bg-savings text-white"
                      : "bg-white text-muted-foreground"
                }`}
              >
                {isDone ? <Check className="h-3 w-3" /> : s.n}
              </span>
              <span>{s.label}</span>
            </div>
            {i < steps.length - 1 && (
              <span
                className={`h-px w-6 sm:w-10 ${isDone ? "bg-savings/40" : "bg-border"}`}
                aria-hidden
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}


/**
 * Optional-field primitives.
 *
 * The hint is a DESCRIPTION, not part of the label. It is rendered outside the
 * `<label>` and wired with `aria-describedby`, so the control's accessible
 * name stays exactly the visible field label while assistive tech still
 * announces the hint.
 */
function Field({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  autoComplete,
  hint,
  required = false,
  requiredWhy,
  recommended = false,
  recommendedActive = false,
  verified = false,
  error,
  onBlur,
  inputRef,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  autoComplete?: string;
  hint?: string;
  /** Highlighted because the readiness check needs this value to continue. */
  required?: boolean;
  requiredWhy?: string;
  /** Optional but useful. Neutral styling — never red, never blocking. */
  recommended?: boolean;
  /** When true, the optional field gets the recommended highlight. */
  recommendedActive?: boolean;
  /**
   * A previously-required value the customer has now supplied. Presentation
   * only: the field keeps the same behaviour but sheds the red "still awaiting
   * action" treatment so a completed form reads as complete.
   */
  verified?: boolean;
  /** Inline message shown after the customer tried to recheck too early. */
  error?: string;
  onBlur?: () => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div
      className={
        required
          ? "block rounded-xl border border-destructive/25 bg-destructive/[0.035] p-4 transition-all duration-200"
          : "block"
      }
      data-required-field={required ? "true" : undefined}
      data-verified-field={verified ? "true" : undefined}
    >
      <label
        htmlFor={id}
        className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
      >
        <span className={required ? "text-navy" : undefined}>
          {label}
          {required ? (
            <span className="ml-1 text-destructive" aria-hidden>
              *
            </span>
          ) : null}
        </span>
        {required ? (
          <span className="rounded-full bg-destructive/[0.10] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-destructive animate-fade-in">
            Required
          </span>
        ) : recommended && recommendedActive ? (
          <span className="text-[9px] font-normal normal-case tracking-normal text-muted-foreground/70">
            Optional
          </span>
        ) : null}
      </label>
      <input
        id={id}
        ref={inputRef}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-describedby={[errorId, hintId].filter(Boolean).join(" ") || undefined}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        className={`mt-2 block w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-foreground outline-none transition-all duration-200 placeholder:text-muted-foreground/70 focus:border-trust focus:shadow-[0_0_0_4px_color-mix(in_oklab,var(--trust)_14%,transparent)] ${
          error
            ? "border-destructive"
            : required
              ? "border-destructive/30"
              : "border-input"
        }`}
      />


      {/* The readiness panel above already explains the "why" in full — the
          field itself stays short on purpose. */}
      {required && requiredWhy ? <span className="sr-only">{requiredWhy}</span> : null}

      {error ? (
        <span
          id={errorId}
          className="mt-1.5 block text-[11px] font-semibold leading-5 text-destructive animate-fade-in"
        >
          {error}
        </span>
      ) : null}

      {hint ? (
        <span
          id={hintId}
          className="mt-1.5 block text-[11px] leading-5 text-muted-foreground/75"
        >
          {hint}
        </span>
      ) : null}
    </div>
  );
}



function SelectField({
  label,
  value,
  onChange,
  options,
  hint,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  options: string[];
  hint?: string;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className="block">
      <label
        htmlFor={id}
        className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
      >
        {label}
      </label>
      <select
        id={id}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={hintId}
        className="mt-2 block w-full rounded-lg border border-input bg-white px-3 py-2.5 text-sm text-foreground outline-none transition-all duration-150 focus:border-trust focus:shadow-[0_0_0_4px_color-mix(in_oklab,var(--trust)_14%,transparent)]"
      >
        <option value="">Choose…</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o[0].toUpperCase() + o.slice(1)}
          </option>
        ))}
      </select>
      {hint ? (
        <span
          id={hintId}
          className="mt-1.5 block text-[11px] leading-5 text-muted-foreground/75"
        >
          {hint}
        </span>
      ) : null}
    </div>
  );
}



/* ---------------- Optional claim information helpers ---------------- */

const STORAGE_REASONS = [
  "Collision",
  "Theft Recovery",
  "Mechanical Breakdown",
  "Police Tow",
  "Abandoned Vehicle",
  "Commercial Vehicle",
  "Other",
];

const REVIEW_FOCUS_OPTIONS = [
  "Review the entire invoice",
  "Reduce storage charges",
  "Verify legal compliance",
  "Prepare for negotiation",
  "Identify billing concerns",
  "I'm not sure",
];

/**
 * Presentation-only nested disclosure for the sections that never gate
 * payment. Children stay mounted when collapsed so typed values survive.
 */
/** Session-scoped open state for the outer disclosure (presentation only). */
const outerOpenState = { open: false, autoOpened: false, manuallyClosed: false };

function MoreOptionalInformation({ children }: { children: React.ReactNode }) {
  const [open, setOpenState] = useState(() => outerOpenState.open);
  const setOpen = useCallback((next: boolean) => {
    outerOpenState.open = next;
    setOpenState(next);
  }, []);
  // Any child that holds persisted customer data auto-reveals this wrapper
  // once, so populated sections are visible after save / reload.
  const reportHasValue = useCallback(
    (hasValue: boolean) => {
      if (hasValue && !outerOpenState.autoOpened && !outerOpenState.manuallyClosed) {
        outerOpenState.autoOpened = true;
        setOpen(true);
      }
    },
    [setOpen],
  );

  const id = useId();
  return (
    <div className="mt-4 rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={() => {
          if (open) outerOpenState.manuallyClosed = true;
          setOpen(!open);
        }}
        aria-expanded={open}
        aria-controls={id}
        className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors duration-200 ease-out hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trust focus-visible:ring-offset-2"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-navy">Additional Case Details</span>
          <span className="mt-0.5 block text-xs text-muted-foreground/70">
            Vehicle, insurance, storage context, review preferences and comments.
          </span>
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      <div id={id} hidden={!open} className="border-t border-border px-4 py-2 sm:px-5">
        <SubsectionContext.Provider value={{ reportHasValue }}>
          <div className="divide-y divide-border/60">{children}</div>
        </SubsectionContext.Provider>
      </div>
    </div>
  );
}

const SubsectionContext = createContext<{
  reportHasValue: (hasValue: boolean) => void;
}>({ reportHasValue: () => {} });

/**
 * Session-scoped open state for the optional subsections.
 *
 * Presentation only — it holds nothing but "is this disclosure open".
 * Keeping it outside the React tree makes the open state survive any remount
 * of the form subtree (autosave, readiness refetch, validation, parent
 * rerenders), so a populated section can never silently collapse. A real page
 * reload clears it, at which point `hasValue` re-opens populated sections.
 */
const subsectionOpenState = new Map<string, { open: boolean; manual: boolean }>();

function SubsectionItem({
  id,
  icon: Icon,
  title,
  helper,
  hasValue = false,
  children,
}: {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  helper: string;
  /** True when this section holds customer-entered/persisted data. */
  hasValue?: boolean;
  children: React.ReactNode;
}) {
  const { reportHasValue } = useContext(SubsectionContext);
  // Defaults are computed once per mount, never re-derived on every render.
  const [expanded, setExpandedState] = useState(() => {
    const saved = subsectionOpenState.get(id);
    if (saved) return saved.open;
    subsectionOpenState.set(id, { open: hasValue, manual: false });
    return hasValue;
  });
  const setExpanded = useCallback(
    (next: boolean, manual: boolean) => {
      const prev = subsectionOpenState.get(id);
      subsectionOpenState.set(id, {
        open: next,
        manual: manual ? true : (prev?.manual ?? false),
      });
      setExpandedState(next);
    },
    [id],
  );
  const previousHasValue = useRef(hasValue);
  useEffect(() => {
    // Auto-expand only on the transition to "has data" (first entry), and only
    // when the customer has not deliberately collapsed this section.
    if (hasValue && !previousHasValue.current && !subsectionOpenState.get(id)?.manual) {
      setExpanded(true, false);
    }
    previousHasValue.current = hasValue;
    reportHasValue(hasValue);
  }, [hasValue, reportHasValue, id, setExpanded]);

  return (
    <div className="py-0.5">
      <button
        type="button"
        onClick={() => setExpanded(!expanded, true)}
        aria-expanded={expanded}
        aria-controls={`${id}-content`}
        className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-xl px-2 py-3 text-left transition-colors duration-200 ease-out hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trust focus-visible:ring-offset-2"
      >
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground/80" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-navy">{title}</span>
          <span className="mt-0.5 block truncate text-xs text-muted-foreground/70">{helper}</span>
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out ${expanded ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      <div
        id={`${id}-content`}
        hidden={!expanded}
        className="overflow-hidden [&:not([hidden])]:animate-fade-in [&:not([hidden])]:[animation-duration:280ms]"
      >
        <div className="border-t border-border/60 pt-4 pb-2">{children}</div>
      </div>
    </div>
  );
}

function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-12 first:mt-0">
      <h3 className="text-[13px] font-bold uppercase tracking-wider text-navy/85">{title}</h3>
      {description ? (
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
      ) : null}
      <div className="mt-4">{children}</div>
    </section>
  );
}



function TextAreaField({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className="block">
      <label
        htmlFor={id}
        className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
      >
        {label} <span className="font-normal normal-case tracking-normal">(optional)</span>
      </label>
      <textarea
        id={id}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={7}
        maxLength={5000}
        aria-describedby={hintId}
        className="mt-2 block min-h-[184px] w-full rounded-lg border border-input bg-white px-3 py-2.5 text-sm text-foreground outline-none transition-all duration-150 placeholder:text-muted-foreground/70 focus:border-trust focus:shadow-[0_0_0_4px_color-mix(in_oklab,var(--trust)_14%,transparent)]"

      />
      {hint ? (
        <span
          id={hintId}
          className="mt-1.5 block text-[11px] leading-5 text-muted-foreground/75"
        >
          {hint}
        </span>
      ) : null}

    </div>
  );
}

